/**
 * @summary LANDING PAGES: This is any landing page for a microsite, https://domain.com/{slug}/landing.
 * This points to wordpress Landing Pages content types
 */
import React from 'react';
import PageContainer from 'components/global/PageContainer';
import PageSectionLayout from 'components/global/PageSectionLayout';
import getLandingPageData from 'app-requests/content-type-loaders/landing-page-props';
import handleServerRedirect, {
  handleMaintenanceRedirect,
} from 'utils/handleServerRedirect';
import { APIError } from 'utils/errors/APIError';
import { APITransformError } from 'utils/errors/APITransformError';
import { SiteTypeError } from 'utils/errors/SiteTypeError';
import { LogError } from 'utils/logging/LogError';
import isDevelopment from 'utils/isDevelopment';
import {
  clickPortalRedirect,
  defaultRedirect,
  notFoundRedirect,
  SITE_TYPE,
} from 'consts';
import { GetServerSidePropsResult } from 'next';
import {
  ErrorPriority,
  ServerSidePageProps,
  TriadGetServerSidePropsContext,
} from 'types';

function LandingPage(props: {
  pageProps: ServerSidePageProps;
}): React.JSX.Element {
  const {
    pageProps: { pageSections, sidebarSections, sidebarRange, ogImg, ...globalPageProps },
  } = props;

  return (
    <PageContainer pageProps={{ ...globalPageProps, ogImg }}>
      <PageSectionLayout
        pageSections={pageSections}
        sidebarSections={sidebarSections}
        sidebarRange={sidebarRange}
        isLanding
      />
    </PageContainer>
  );
}

/**
 * @summary this is the Nextjs function that will get the server side props.
 * this will call all needed API to create the Landing Page
 */
export async function getServerSideProps(
  context: TriadGetServerSidePropsContext
): Promise<GetServerSidePropsResult<ServerSidePageProps | object>> {
  try {
    const pageProps = await getLandingPageData(context);
    return { props: pageProps };
  } catch (error: unknown) {
    if (
      error instanceof APIError &&
      error.statusCode === 404 &&
      context.req.url !== defaultRedirect
    ) {
      handleServerRedirect(defaultRedirect, context);
      return { props: {} };
    }

    if (error instanceof APIError && error.statusCode === 404) {
      handleServerRedirect(notFoundRedirect, context);
      return { props: {} };
    }

    if (
      error instanceof SiteTypeError &&
      error.siteType === SITE_TYPE.CLICK_PORTAL
    ) {
      handleServerRedirect(clickPortalRedirect, context);
      return { props: {} };
    }

    if (error instanceof APIError) {
      LogError(error, {
        description: `statusCode: ${error.statusCode}, endpoint: ${error.endpoint}, route: landing`,
        priority: ErrorPriority.ONE,
        endpoint: error.endpoint,
      });
    } else if (error instanceof APITransformError) {
      LogError(error, {
        description: `method: ${error.method}, route: landing`,
        priority: ErrorPriority.ONE,
      });
    } else {
      LogError(error as Error, {
        description: `route: landing`,
        priority: ErrorPriority.ONE,
      });
    }

    if (isDevelopment()) {
      throw error;
    } else {
      handleMaintenanceRedirect(context, error as Error);
    }

    return { props: {} };
  }
}

export default LandingPage;
